import { useEffect, useState } from 'react'
import { type GalleryAlbum } from '../components/Gallery'
import { API_BASE_URL } from '../config/api'

interface Event {
  id: number
  title: string
  date: string
  location?: string
}

const GalleryPage = () => {
  const [selectedAlbum, setSelectedAlbum] = useState<GalleryAlbum | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [galleryAlbums, setGalleryAlbums] = useState<GalleryAlbum[]>([])
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)


  // Default gallery albums (fallback if API fails or no galleries)
  const defaultGalleryAlbums: GalleryAlbum[] = [
    {
      id: 1,
      title: 'Event Flyers & Posters',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      images: [
        { id: 1, googledrivelink: '', src: `${import.meta.env.BASE_URL}aashiqui-banner.webp`, alt: 'Aashiqui concert banner', title: 'Aashiqui With Kumar Sanu & Sadhana Sargam' },
        { id: 2, googledrivelink: '', src: `${import.meta.env.BASE_URL}Kumar-Sanu-updated-online-flye.webp`, alt: 'Kumar Sanu flyer', title: 'Kumar Sanu Live' },
        { id: 3, googledrivelink: '', src: `${import.meta.env.BASE_URL}New-flyer-with-sponsor.webp`, alt: 'Event flyer with sponsors', title: 'Event Sponsors' },
        { id: 4, googledrivelink: '', src: `${import.meta.env.BASE_URL}aashiqui-banner.webp`, alt: 'QR code ticket flyer', title: 'Scan to Buy Tickets' },
      ],
    },
    {
      id: 2,
      title: 'Meet & Greet',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}Meet-Greet-50.webp`,
      images: [
        { id: 5, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-50.webp`, alt: 'Meet and Greet 50', title: 'Meet & Greet Moments' },
        { id: 6, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-51.webp`, alt: 'Meet and Greet 51', title: 'Fans with Artists' },
        { id: 7, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-52.webp`, alt: 'Meet and Greet 52', title: 'Backstage Candid' },
        { id: 8, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-55.webp`, alt: 'Meet and Greet 55', title: 'Celebration Together' },
      ],
    },
    {
      id: 3,
      title: 'Event Performances',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}superhit.webp`,
      images: [
        { id: 9, googledrivelink: '', src: `${import.meta.env.BASE_URL}superhit.webp`, alt: 'Superhit event collage', title: 'Superhit Performances' },
        { id: 10, googledrivelink: '', src: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=800&q=80', alt: 'Concert stage setup', title: 'Concert Stage' },
        { id: 11, googledrivelink: '', src: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80', alt: 'Live performance', title: 'Live Performance' },
        { id: 12, googledrivelink: '', src: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&q=80', alt: 'Crowd at event', title: 'Excited Audience' },
      ],
    },
  ]

  // Helper function to get gallery image URL
  const getGalleryImageUrl = (image: any): string => {
    if (image.media_url) return image.media_url
    if (image.image_url) return image.image_url
    if (image.image_path) {
      const cleanPath = image.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return ''
  }

  // Fetch events
  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        setEvents(result.data)
      }
    } catch (err) {
      console.error('Error fetching events:', err)
    }
  }

  // Fetch event galleries
  const fetchEventGalleries = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_list.php`)
      const result = await response.json()
      
      if (result.status && result.data && result.data.length > 0) {
        // Group images by event_id
        const galleryMap = new Map<number, any>()
        
        result.data.forEach((image: any) => {
          if (!galleryMap.has(image.event_id)) {
            // Find event name from events array
            const event = events.find(e => e.id === image.event_id)
            const eventName = event ? event.title : `Event #${image.event_id}`
            
            // Get thumbnail/cover image (prefer thumbnail, otherwise first image)
            const thumbnailImage = result.data.find((img: any) => 
              img.event_id === image.event_id && img.is_thumbnail === 1
            )
            const firstImage = result.data.find((img: any) => img.event_id === image.event_id)
            const coverImageUrl = thumbnailImage 
              ? getGalleryImageUrl(thumbnailImage)
              : (firstImage ? getGalleryImageUrl(firstImage) : getGalleryImageUrl(image))
            
            galleryMap.set(image.event_id, {
              id: image.event_id,
              title: eventName,
              googledrivelink: image.googledrivelink,
              coverImage: coverImageUrl || `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
              images: []
            })
          }
          
          const gallery = galleryMap.get(image.event_id)!
          const imageUrl = getGalleryImageUrl(image)
          if (imageUrl) {
           
            gallery.images.push({
              id: image.id,
              src: imageUrl,
              alt: image.title || `Gallery Image ${gallery.images.length + 1}`,
              title: image.title,
              googledrivelink: image.googledrivelink,
            })
          }
        })
        
        // Convert to array and sort by event_id (most recent first)
        const galleriesArray = Array.from(galleryMap.values())
          .filter(gallery => gallery.images.length > 0) // Only include galleries with images
          .sort((a, b) => b.id - a.id) // Sort by event_id descending (newest first)
        
        setGalleryAlbums(galleriesArray.length > 0 ? galleriesArray : defaultGalleryAlbums)
      } else {
        // No galleries from API, use defaults
        setGalleryAlbums(defaultGalleryAlbums)
      }
    } catch (err) {
      console.error('Error fetching event galleries:', err)
      // Use default galleries on error
      setGalleryAlbums(defaultGalleryAlbums)
    } finally {
      setLoading(false)
    }
  }

  // Fetch events on mount
  useEffect(() => {
    fetchEvents()
  }, [])

  // Fetch galleries after events are loaded
  useEffect(() => {
    if (events.length > 0) {
      fetchEventGalleries()
    } else {
      // If events fail to load, still try to fetch galleries (event names will be generic)
      fetchEventGalleries()
    }
  }, [events])

  const currentImages = selectedAlbum ? selectedAlbum.images : []

  const handleAlbumClick = (album: GalleryAlbum) => {
    setSelectedAlbum(album)
  }

  const handleBackToAlbums = () => {
    setSelectedAlbum(null)
  }

  const openLightbox = (index: number) => {
    setActiveIndex(index)
    setIsOpen(true)
  }

  const closeLightbox = () => setIsOpen(false)

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length)
  }

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % currentImages.length)
  }

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') goToNext()
      if (e.key === 'ArrowLeft') goToPrevious()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, currentImages.length])

  return (
    <div className="w-full min-h-screen bg-gray-900 py-8 md:py-12">
      <section className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
            Gallery
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
            Our Event Gallery
          </h1>
          {selectedAlbum && (
            <p className="text-lg text-gray-300">
              {selectedAlbum.title}
            </p>
          )}
        </div>

        {loading ? (
          /* Loading State */
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-light mx-auto mb-4"></div>
              <p className="text-gray-400 text-lg">Loading galleries...</p>
            </div>
          </div>
        ) : !selectedAlbum ? (
          /* Album View - Show All Albums */
          galleryAlbums.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400 text-lg">No galleries available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
              {galleryAlbums.map((album) => (
              <div
                key={album.id}
                className="relative group overflow-hidden rounded-lg aspect-square cursor-pointer bg-gray-700"
                onClick={() => handleAlbumClick(album)}
              >
                <img
                  src={album.coverImage}
                  alt={album.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-end p-6">
                  <div className="w-full">
                    <h3 className="text-primary-light font-bold text-xl mb-2">{album.title}</h3>
                    <p className="text-gray-300 text-sm">{album.images.length} photos</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-primary-light/90 backdrop-blur-sm rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
              ))}
            </div>
          )
        ) : (
          /* Album Images View */
          <>
            <div className="mb-8">
              <button
                onClick={handleBackToAlbums}
                className="inline-flex items-center text-primary-light hover:text-primary-dark font-semibold transition-colors mb-6"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Albums 
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
              {currentImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative group overflow-hidden rounded-lg aspect-square cursor-pointer transform hover:scale-105 transition-all duration-300"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    {image.title && (
                      <p className="text-white font-semibold text-sm line-clamp-2">{image.title}</p>
                    )}
                  </div>
                  <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>

            {currentImages.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">No images found in this album.</p>
              </div>
            )}
          </>
        )}

        {selectedAlbum?.googledrivelink != null &&
            
            <div className="inset-0 flex items-center justify-center mt-8">
              <a
                href={selectedAlbum?.googledrivelink}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer bg-primary-light hover:bg-primary-dark text-white px-4 py-3 rounded-lg font-semibold transition-colors text-sm md:text-base inline-block text-center"
              >
                View More
              </a>
            </div>
        }

        {/* Back to Home */}
        {/* <div className="text-center mt-12">
          <Link
            to="/"
            className="inline-flex items-center text-primary-light hover:text-primary-dark font-semibold transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div> */}
      </section>

      {/* Lightbox Modal */}
      {isOpen && currentImages[activeIndex] && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center px-4 animate-fadeIn">
          <div className="absolute inset-0" onClick={closeLightbox}></div>

          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20 transition-all duration-300"
            aria-label="Close gallery"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {currentImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute left-6 sm:left-10 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20 transition-all duration-300"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-6 sm:right-10 text-white bg-black/60 hover:bg-black/80 p-3 rounded-full z-20 transition-all duration-300"
                aria-label="Next image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          <div className="relative max-w-6xl w-full z-10">
            <div className="relative overflow-hidden rounded-xl shadow-2xl bg-black/40">
              <img
                src={currentImages[activeIndex].src}
                alt={currentImages[activeIndex].alt}
                className="w-full max-h-[85vh] object-contain"
              />
              {(currentImages[activeIndex].title || currentImages[activeIndex].alt) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                  <p className="text-white text-xl font-semibold mb-1">
                    {currentImages[activeIndex].title || currentImages[activeIndex].alt}
                  </p>
                  <p className="text-gray-300 text-sm">
                    {activeIndex + 1} of {currentImages.length}
                  </p>
                </div>
              )}
            </div>

            {currentImages.length > 1 && (
              <div className="flex justify-center space-x-2 mt-6">
                {currentImages.map((img, index) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === activeIndex ? 'w-8 bg-primary-light' : 'w-2 bg-white/60 hover:bg-white'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default GalleryPage

